import type React from "react";
import { MainMenu } from "@excalidraw/excalidraw";
import type { AccountInfo } from "@azure/msal-browser";
import { useProfilePhoto } from "../hooks/useProfilePhoto";

interface ReadOnlyMainMenuProps {
  account?: AccountInfo | null;
  onOpenInEditor?: () => void;
  onAbout?: () => void;
  onSignOut?: () => void;
}

/**
 * Custom <MainMenu> for the read-only ShareViewPage.
 *
 * Aligned with CustomMainMenu labels where the action is identical.
 *
 * Order:
 *   1. Open in editor                ← also available on the banner button
 *   ---
 *   2. Save to... (upstream Export)
 *   3. Export image... (upstream SaveAsImage)
 *   ---
 *   5. Help (upstream)
 *   6. About
 *   ---
 *   7. profile row
 *   8. Sign out
 *
 * Items explicitly NOT included (vs. CustomMainMenu): New drawing, Live
 * collaboration, cloud save, Load scene from file, Change
 * background color, Reset the canvas. Each is either out of context for
 * a viewed snapshot or destructive on read-only content.
 */
export default function ReadOnlyMainMenu({
  account,
  onOpenInEditor,
  onAbout,
  onSignOut,
}: ReadOnlyMainMenuProps) {
  const photoUrl = useProfilePhoto();
  // `||` instead of `??`: AccountInfo fields can be empty strings (not just
  // null/undefined), and an empty string would otherwise propagate into the
  // initials computation.
  const displayName = account?.name || account?.username || "User";
  const initials = getInitials(displayName);

  return (
    <MainMenu>
      {onOpenInEditor && (
        <>
          <MainMenu.Item onSelect={onOpenInEditor} icon={openInEditorIcon}>
            Open in editor
          </MainMenu.Item>
          <MainMenu.Separator />
        </>
      )}

      <MainMenu.DefaultItems.Export />
      <MainMenu.DefaultItems.SaveAsImage />

      <MainMenu.Separator />

      <MainMenu.DefaultItems.Help />
      {onAbout && (
        <MainMenu.Item onSelect={onAbout} icon={aboutIcon}>
          About
        </MainMenu.Item>
      )}

      <MainMenu.Separator />

      {account && (
        <MainMenu.ItemCustom style={profileItemStyle}>
          {photoUrl ? (
            <img
              src={photoUrl}
              alt={displayName}
              style={menuAvatarStyle}
              referrerPolicy="no-referrer"
            />
          ) : (
            <span style={menuInitialsStyle}>{initials}</span>
          )}
          <span style={menuNameStyle}>{displayName}</span>
        </MainMenu.ItemCustom>
      )}

      {onSignOut && (
        <MainMenu.Item onSelect={onSignOut} icon={signOutIcon}>
          Sign out
        </MainMenu.Item>
      )}
    </MainMenu>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getInitials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
}

// ---------------------------------------------------------------------------
// Icons (subset duplicated from CustomMainMenu to avoid coupling the two)
// ---------------------------------------------------------------------------

const openInEditorIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </svg>
);

const aboutIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const signOutIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

// ---------------------------------------------------------------------------
// Styles (mirror CustomMainMenu so the profile row visually matches)
// ---------------------------------------------------------------------------

const profileItemStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "8px 12px",
};

const menuAvatarStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: "50%",
  objectFit: "cover",
  flexShrink: 0,
};

const menuInitialsStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "var(--color-primary)",
  color: "var(--color-on-accent)",
  fontSize: 12,
  fontWeight: 600,
  flexShrink: 0,
};

const menuNameStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
